import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  XMarkIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ClockIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { campaignApi } from '../../api';

const EMAIL_STATUS_COLORS = {
  PENDING: 'bg-gray-100 text-gray-800',
  SENT: 'bg-blue-100 text-blue-800',
  FAILED: 'bg-red-100 text-red-800',
  OPENED: 'bg-green-100 text-green-800',
  CLICKED: 'bg-purple-100 text-purple-800',
  BOUNCED: 'bg-orange-100 text-orange-800',
};

const WHATSAPP_STATUS_COLORS = {
  PENDING: 'bg-gray-100 text-gray-800',
  SENT: 'bg-blue-100 text-blue-800',
  DELIVERED: 'bg-emerald-100 text-emerald-800',
  READ: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
};

const CampaignAnalyticsModal = ({ show, campaign, onClose }) => {
  const [analytics, setAnalytics] = useState(null);
  const [logs, setLogs] = useState({ logs: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [logPage, setLogPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [logPageSize, setLogPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const isWhatsappCampaign = useMemo(() => campaign?.channel === 'WHATSAPP', [campaign?.channel]);

  const fetchAnalytics = useCallback(async () => {
    if (!campaign) return;
    setLoading(true);
    try {
      const data = isWhatsappCampaign
        ? await campaignApi.getWhatsappAnalytics(campaign.id)
        : await campaignApi.getAnalytics(campaign.id);
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [campaign, isWhatsappCampaign]);

  const fetchLogs = useCallback(async () => {
    if (!campaign) return;
    try {
      const data = isWhatsappCampaign
        ? await campaignApi.getWhatsappLogs(
          campaign.id,
          logPage,
          logPageSize,
          statusFilter || null
        )
        : await campaignApi.getEmailLogs(
          campaign.id,
          logPage,
          logPageSize,
          statusFilter || null,
          sortBy || null,
          sortDir || null
        );
      setLogs(data || { logs: [], total: 0 });
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  }, [campaign, isWhatsappCampaign, logPage, logPageSize, statusFilter, sortBy, sortDir]);

  useEffect(() => {
    if (!show || !campaign) return;
    fetchAnalytics();
    fetchLogs();
  }, [show, campaign, fetchAnalytics, fetchLogs]);

  useEffect(() => {
    if (!show || !campaign) return;

    const intervalId = setInterval(() => {
      fetchAnalytics();
      fetchLogs();
    }, 7000);

    return () => clearInterval(intervalId);
  }, [show, campaign, fetchAnalytics, fetchLogs]);

  useEffect(() => {
    if (!show || !campaign) return;
    fetchLogs();
  }, [show, campaign, logPage, logPageSize, statusFilter, sortBy, sortDir, fetchLogs]);

  useEffect(() => {
    setPageInput(logPage.toString());
  }, [logPage]);

  useEffect(() => {
    setLogPage(1);
    setStatusFilter('');
    setSortBy('');
    setSortDir('desc');
  }, [campaign?.id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (!show) return null;

  const statusColors = isWhatsappCampaign ? WHATSAPP_STATUS_COLORS : EMAIL_STATUS_COLORS;
  const maxPage = Math.max(1, Math.ceil((logs.total || 0) / logPageSize));

  return (
    <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative top-5 mx-auto mb-10 w-full max-w-5xl rounded-md border bg-white p-6 shadow-lg">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Campaign Analytics</h3>
            <p className="mt-1 text-sm text-gray-500">{campaign?.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <ChartBarIcon className="mr-2 inline h-5 w-5" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'logs'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {isWhatsappCampaign ? (
                <ChatBubbleLeftRightIcon className="mr-2 inline h-5 w-5" />
              ) : (
                <EnvelopeIcon className="mr-2 inline h-5 w-5" />
              )}
              {isWhatsappCampaign ? 'WhatsApp Logs' : 'Email Logs'}
            </button>
          </nav>
        </div>

        {loading ? (
          <div className="py-12 text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-purple-500 border-t-transparent"></div>
            <p className="mt-2 text-sm text-gray-500">Loading analytics...</p>
          </div>
        ) : activeTab === 'overview' ? (
          <div>
            {analytics && !isWhatsappCampaign && (
              <>
                <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div className="rounded-lg bg-gray-50 p-4">
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-8 w-8 text-gray-400" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-500">Total Emails</p>
                        <p className="text-2xl font-semibold text-gray-900">{analytics.totalEmails}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg bg-blue-50 p-4">
                    <div className="flex items-center">
                      <CheckCircleIcon className="h-8 w-8 text-blue-400" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-blue-600">Sent</p>
                        <p className="text-2xl font-semibold text-blue-900">{analytics.sent}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg bg-green-50 p-4">
                    <div className="flex items-center">
                      <EyeIcon className="h-8 w-8 text-green-400" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-green-600">Opened</p>
                        <p className="text-2xl font-semibold text-green-900">{analytics.opened}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg bg-red-50 p-4">
                    <div className="flex items-center">
                      <XCircleIcon className="h-8 w-8 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-red-600">Failed</p>
                        <p className="text-2xl font-semibold text-red-900">{analytics.failed}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="rounded-lg border bg-white p-4">
                    <h4 className="mb-3 text-sm font-medium text-gray-700">Delivery Rate</h4>
                    <span className="text-3xl font-bold text-gray-900">{analytics.deliveryRate?.toFixed(1)}%</span>
                    <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full bg-blue-600"
                        style={{ width: `${Math.min(analytics.deliveryRate || 0, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="rounded-lg border bg-white p-4">
                    <h4 className="mb-3 text-sm font-medium text-gray-700">Open Rate</h4>
                    <span className="text-3xl font-bold text-gray-900">{analytics.openRate?.toFixed(1)}%</span>
                    <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full bg-green-600"
                        style={{ width: `${Math.min(analytics.openRate || 0, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {analytics && isWhatsappCampaign && (
              <>
                <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-6">
                  <div className="rounded-lg bg-gray-50 p-4">
                    <p className="text-xs font-medium text-gray-500">Total</p>
                    <p className="text-2xl font-semibold text-gray-900">{analytics.totalMessages || 0}</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4">
                    <p className="text-xs font-medium text-gray-500">Pending</p>
                    <p className="text-2xl font-semibold text-gray-900">{analytics.pending || 0}</p>
                  </div>
                  <div className="rounded-lg bg-blue-50 p-4">
                    <p className="text-xs font-medium text-blue-600">Sent</p>
                    <p className="text-2xl font-semibold text-blue-900">{analytics.sent || 0}</p>
                  </div>
                  <div className="rounded-lg bg-emerald-50 p-4">
                    <p className="text-xs font-medium text-emerald-600">Delivered</p>
                    <p className="text-2xl font-semibold text-emerald-900">{analytics.delivered || 0}</p>
                  </div>
                  <div className="rounded-lg bg-green-50 p-4">
                    <p className="text-xs font-medium text-green-600">Read</p>
                    <p className="text-2xl font-semibold text-green-900">{analytics.read || 0}</p>
                  </div>
                  <div className="rounded-lg bg-red-50 p-4">
                    <p className="text-xs font-medium text-red-600">Failed</p>
                    <p className="text-2xl font-semibold text-red-900">{analytics.failed || 0}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="rounded-lg border bg-white p-4">
                    <h4 className="mb-3 text-sm font-medium text-gray-700">Delivery Rate</h4>
                    <span className="text-3xl font-bold text-gray-900">{(analytics.deliveryRate || 0).toFixed(1)}%</span>
                    <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full bg-blue-600"
                        style={{ width: `${Math.min(analytics.deliveryRate || 0, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="rounded-lg border bg-white p-4">
                    <h4 className="mb-3 text-sm font-medium text-gray-700">Read Rate</h4>
                    <span className="text-3xl font-bold text-gray-900">{(analytics.readRate || 0).toFixed(1)}%</span>
                    <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full bg-green-600"
                        style={{ width: `${Math.min(analytics.readRate || 0, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {analytics && analytics.pending > 0 && (
              <div className="mt-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <div className="flex items-center">
                  <ClockIcon className="mr-2 h-5 w-5 text-yellow-600" />
                  <span className="text-sm text-yellow-800">
                    <strong>{analytics.pending}</strong> {isWhatsappCampaign ? 'messages' : 'emails'} are still pending
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Filter by status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setLogPage(1);
                  }}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-purple-500 focus:ring-purple-500"
                >
                  <option value="">All</option>
                  {isWhatsappCampaign ? (
                    <>
                      <option value="PENDING">Pending</option>
                      <option value="SENT">Sent</option>
                      <option value="DELIVERED">Delivered</option>
                      <option value="READ">Read</option>
                      <option value="FAILED">Failed</option>
                    </>
                  ) : (
                    <>
                      <option value="PENDING">Pending</option>
                      <option value="SENT">Sent</option>
                      <option value="FAILED">Failed</option>
                      <option value="OPENED">Opened</option>
                      <option value="BOUNCED">Bounced</option>
                    </>
                  )}
                </select>
              </div>

              {!isWhatsappCampaign && (
                <>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Sort by:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => {
                        setSortBy(e.target.value);
                        setLogPage(1);
                      }}
                      className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-purple-500 focus:ring-purple-500"
                    >
                      <option value="">Default</option>
                      <option value="openedCount">Times Opened</option>
                      <option value="sentAtUtc">Sent Date</option>
                      <option value="openedAtUtc">Opened Date</option>
                    </select>
                    {sortBy && (
                      <select
                        value={sortDir}
                        onChange={(e) => {
                          setSortDir(e.target.value);
                          setLogPage(1);
                        }}
                        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-purple-500 focus:ring-purple-500"
                      >
                        <option value="desc">Descending</option>
                        <option value="asc">Ascending</option>
                      </select>
                    )}
                  </div>
                </>
              )}

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Limit:</label>
                <select
                  value={logPageSize}
                  onChange={(e) => {
                    setLogPageSize(Number(e.target.value));
                    setLogPage(1);
                  }}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-purple-500 focus:ring-purple-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {isWhatsappCampaign ? (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Recipient Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Sent At</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Delivered At</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Read At</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">WAM ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Error</th>
                      </>
                    ) : (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Recipient</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Sent At</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Opened</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Error</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {logs.logs?.length === 0 ? (
                    <tr>
                      <td colSpan={isWhatsappCampaign ? 7 : 5} className="px-4 py-8 text-center text-gray-500">
                        No logs found
                      </td>
                    </tr>
                  ) : (
                    logs.logs?.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        {isWhatsappCampaign ? (
                          <>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{log.recipientPhone || '-'}</td>
                            <td className="whitespace-nowrap px-4 py-3">
                              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[log.status] || statusColors.PENDING}`}>
                                {log.status}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{formatDate(log.sentAtUtc)}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{formatDate(log.deliveredAtUtc)}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{formatDate(log.readAtUtc)}</td>
                            <td className="max-w-xs truncate px-4 py-3 text-xs text-gray-500" title={log.wamId || ''}>{log.wamId || '-'}</td>
                            <td className="max-w-xs px-4 py-3">
                              {log.errorMessage ? (
                                <p className="truncate text-xs text-red-600" title={log.errorMessage}>{log.errorMessage}</p>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="whitespace-nowrap px-4 py-3">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{log.recipientName || 'Unknown'}</p>
                                <p className="text-xs text-gray-500">{log.recipientEmail}</p>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[log.status] || statusColors.PENDING}`}>
                                {log.status}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{formatDate(log.sentAtUtc)}</td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {log.openedAtUtc ? (
                                <div>
                                  <p className="text-sm text-green-600">{formatDate(log.openedAtUtc)}</p>
                                  <p className="text-xs text-gray-500">{log.openedCount} time{log.openedCount !== 1 ? 's' : ''}</p>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400">-</span>
                              )}
                            </td>
                            <td className="max-w-xs px-4 py-3">
                              {log.errorMessage ? (
                                <p className="truncate text-xs text-red-600" title={log.errorMessage}>{log.errorMessage}</p>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {logs.total > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-gray-500">Showing {logs.logs?.length} of {logs.total} logs</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setLogPage(Math.max(1, logPage - 1))}
                    disabled={logPage === 1}
                    className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>

                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-700">Page</span>
                    <input
                      type="number"
                      min="1"
                      max={maxPage}
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onBlur={() => {
                        const value = parseInt(pageInput, 10);
                        if (value >= 1 && value <= maxPage) {
                          setLogPage(value);
                        } else {
                          setPageInput(logPage.toString());
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const value = parseInt(pageInput, 10);
                          if (value >= 1 && value <= maxPage) {
                            setLogPage(value);
                          } else {
                            setPageInput(logPage.toString());
                          }
                        }
                      }}
                      className="w-16 rounded-md border border-gray-300 px-2 py-1 text-center text-sm focus:border-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">of {maxPage}</span>
                  </div>

                  <button
                    onClick={() => setLogPage(logPage + 1)}
                    disabled={logPage >= maxPage}
                    className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end border-t pt-4">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignAnalyticsModal;
